/**
 * Which queries get written to the persisted (IndexedDB) cache.
 *
 * This is an exclusion allowlist: persist every successfully-resolved query
 * except a small denylist of identity/permission-bearing or volatile keys. With
 * hundreds of query hooks that grow over time, excluding the few unsafe keys is
 * far lower-maintenance than enumerating every cacheable prefix, and it fails
 * safe toward "cache a harmless list" rather than "silently stop caching a new
 * feature's table".
 *
 * See `query-defaults.ts` for the matching in-memory cache-policy matrix and
 * `docs/frontend-caching.md` for the full rationale.
 */

import type { Query } from '@tanstack/react-query'

/**
 * Top-level query keys that must never leave memory:
 * - identity / permission-bearing (must always be fresh, cheap to refetch)
 * - volatile session-scoped state that is meaningless across a reload
 */
export const NEVER_PERSIST_KEYS: ReadonlySet<string> = new Set([
  'org-context',
  'session',
  'staff',
  'wizard-session',
  // Staff cache observability - volatile, cross-tenant operational data that
  // must not be written to disk (matching LIST_LIVE registration).
  'staff-cache-stats',
  'staff-cache-keys',
  // A simulated regroup, not a fact about the world. Restoring one from disk
  // could show an impact count for a structure that has since changed.
  'partnership-regroup-preview',
  // In-flight merge status; a stale 'running' restored from disk is a lie.
  'merge-plan',
  'merge-impact',
  // A draft address typed into the edit dialog - never written to disk.
  'customer-address-check',
  // Proof links carry upload tokens: anyone holding one can upload to the
  // inspection, so they never leave memory.
  'inspection-proof-links',
])

/**
 * Key *prefixes* that must never leave memory, for cases where the top-level
 * key is too coarse to deny outright.
 *
 * `workflows` covers plenty that is safely cacheable (history, state), but the
 * exception catalogue underneath it is authorisation-shaped: it decides whether
 * an action is gated behind an exception at all. A stale copy silently drops
 * that gate, so an override goes uncaptured until the blob expires - which is
 * exactly what happened when new bindings were added while a session was warm.
 * It is small and fetched once, so keeping it live costs nothing.
 */
export const NEVER_PERSIST_KEY_PREFIXES: ReadonlyArray<readonly string[]> = [
  ['workflows', 'exceptions'],
]

function matchesNeverPersistPrefix(queryKey: readonly unknown[]): boolean {
  return NEVER_PERSIST_KEY_PREFIXES.some(
    (prefix) =>
      prefix.length <= queryKey.length &&
      prefix.every((segment, index) => queryKey[index] === segment),
  )
}

export function shouldPersistQuery(query: Query): boolean {
  const queryKey = query.queryKey as unknown[]
  const topLevelKey = queryKey[0]

  if (typeof topLevelKey !== 'string') return false
  if (NEVER_PERSIST_KEYS.has(topLevelKey)) return false
  if (matchesNeverPersistPrefix(queryKey)) return false

  // Only persist queries that resolved successfully. Excludes 'pending'
  // (nothing to paint) and 'error' (would paint a stale error before the
  // background refetch lands). The explicit error check documents intent for
  // the edge case where status is 'success' mid-retry-after-error.
  if (query.state.status !== 'success') return false
  if (query.state.error != null) return false

  return true
}
